import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { BookOpen, Menu, X, Smartphone } from "lucide-react";
import { useState } from "react";
import { startLifeguideHandoff } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import logo from "@/assets/proverb-logo.webp";
import { usePwa } from "@/context/PwaContext";
import { PwaInstall } from "@/components/PwaInstall";

const NAV = [
  { to: "/", label: "Find Wisdom" },
  { to: "/daily", label: "Wisdom for You" },
  { to: "/send", label: "Send Wisdom" },
  { to: "/saved", label: "Saved Wisdom" },
];

async function goLifeguide() {
  track("bridge_click", { path: window.location.pathname, source: "nav" });
  try {
    const r = await startLifeguideHandoff({ topic: "", context: "nav" });
    if (!r.ok) toast.error("Could not open LifeGuide right now. Please try again.");
  } catch { toast.error("Could not open LifeGuide right now. Please try again."); }
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const pwa = usePwa();

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 lg:h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
            <img src={logo} alt="I Need a Proverb" className="h-9 sm:h-10 lg:h-[70px] w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-6">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.to === "/" ? "find" : n.to.slice(1)}`}
                className={({ isActive }) => `text-sm lg:text-base transition-colors hover:text-[#2C4C3B] ${isActive ? "text-[#2C4C3B] font-semibold" : "text-[#475569]"}`}>
                {n.label}
              </NavLink>
            ))}
            <button data-testid="nav-lifeguide" onClick={goLifeguide} className="text-sm lg:text-base text-[#B8912F] hover:text-[#946f1f] transition-colors">LifeGuide →</button>
            <a data-testid="nav-contact" target="_top" href="mailto:wisdom@ineedaproverb.com?subject=Question%20About%20I%20Need%20a%20Proverb" className="text-sm lg:text-base text-[#475569] hover:text-[#2C4C3B] transition-colors">Contact Us</a>
            <button data-testid="nav-save-phone" onClick={() => pwa?.openInstall()} className="text-sm lg:text-base text-[#475569] hover:text-[#2C4C3B] transition-colors">Save to Phone</button>
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" data-testid="nav-admin" className="text-sm lg:text-base text-[#B8912F] font-semibold">Admin</Link>}
                <Link to="/account" data-testid="nav-account" className="text-sm lg:text-base text-[#475569] hover:text-[#2C4C3B]">Account</Link>
                <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }}
                  className="text-sm lg:text-base px-4 py-2 rounded-full border border-[#E2E8F0] hover:border-[#2C4C3B] transition-colors">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="text-sm lg:text-base text-[#475569] hover:text-[#2C4C3B]">Log in</Link>
                <Link to="/register" data-testid="nav-register" className="text-sm lg:text-base px-4 py-2 rounded-full bg-[#2C4C3B] text-white hover:bg-[#1E3629] transition-colors">Create account</Link>
              </>
            )}
          </div>
          <button className="lg:hidden" data-testid="mobile-menu-btn" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6 text-[#2C4C3B]" /> : <Menu className="w-6 h-6 text-[#2C4C3B]" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-[#E2E8F0] bg-[#FDFBF7] px-5 py-4 flex flex-col gap-3">
            {NAV.map((n) => <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-[#475569]">{n.label}</Link>)}
            <button onClick={() => { setOpen(false); goLifeguide(); }} className="text-left text-[#B8912F] font-medium">LifeGuide →</button>
            <a target="_top" href="mailto:wisdom@ineedaproverb.com?subject=Question%20About%20I%20Need%20a%20Proverb" onClick={() => setOpen(false)} className="text-[#475569]">Contact Us</a>
            <button data-testid="mobile-save-phone" onClick={() => { setOpen(false); pwa?.openInstall(); }} className="text-left text-[#475569]">Save I Need a Proverb to Your Phone</button>
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-[#B8912F] font-semibold">Admin</Link>}
                <Link to="/account" onClick={() => setOpen(false)}>Account</Link>
                <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="text-left text-[#475569]">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)}>Log in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="text-[#2C4C3B] font-semibold">Create account</Link>
              </>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-20 border-t border-[#E2E8F0] bg-[#F4F1EA]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
          <div className="grid md:grid-cols-4 gap-10">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-5 h-5 text-[#2C4C3B]" strokeWidth={1.6} />
                <span className="font-serif-display text-xl font-semibold text-[#2C4C3B]">I Need a Proverb</span>
              </div>
              <p className="text-sm text-[#475569] leading-relaxed">Find, receive, save, and share biblical wisdom for what you're facing — drawn from Proverbs, Psalms and more (World English Bible).</p>
              <button data-testid="footer-save-phone" onClick={() => pwa?.openInstall()} className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#2C4C3B] font-medium hover:underline"><Smartphone className="w-4 h-4" strokeWidth={1.8} /> Save I Need a Proverb to Your Phone</button>
            </div>
            <FooterCol title="Explore" links={[["/send","Send Wisdom"],["/daily","A Proverb for Today"],["/browse-issues","Browse by Issue"],["/browse-all","All Proverbs"],["/library","Download Library"]]} />
            <FooterCol title="Your Space" links={[["/saved","My Saved Wisdom"],["/favorites","Favorites"],["/journal","Journal"],["/account","Account"]]} />
            <FooterCol title="Information" links={[["/about","About"],["/contact","Contact"],["/scripture-info","Scripture & Translation"],["/privacy","Privacy Policy"],["/terms","Terms of Service"],["/harborstone","Church Branding Demo"]]} />
          </div>
          <div className="mt-12 pt-8 border-t border-[#E2E8F0]">
            <p className="text-xs text-[#94A3B8] leading-relaxed max-w-3xl">
              <strong className="text-[#475569]">Disclaimer:</strong> I Need a Proverb provides Scripture-based educational and spiritual-growth content. It is not a substitute for professional medical, mental-health, legal, financial, emergency, or pastoral assistance. If you are in crisis or danger, please contact local emergency services or a qualified professional immediately.
            </p>
            <p className="text-xs text-[#94A3B8] mt-4">© {new Date().getFullYear()} I Need a Proverb · ineedaproverb.com</p>
          </div>
        </div>
      </footer>
      <Toaster position="top-center" richColors />
      <PwaInstall />
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-widest text-[#94A3B8] mb-4">{title}</h4>
      <ul className="space-y-2.5">
        {links.map(([to, label]) => (
          <li key={to}><Link to={to} className="text-sm text-[#475569] hover:text-[#2C4C3B] transition-colors">{label}</Link></li>
        ))}
      </ul>
    </div>
  );
}
